import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { cn, inventoryColorClass } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DataTable,
  type DataTableColumn,
  type DataTableMobileRenderContext,
} from '@/components/ui/data-table';
import { FilterChips } from '@/components/ui/filter-chips';
import { NurseryPriceEditor } from '@/components/NurseryPriceEditor';
import { VariantEditTable } from '@/components/VariantEditTable';
import { ProductLinks } from '@/components/ProductLinks';
import { ProductStatusBadge } from '@/components/StatusBadge';
import { MobileMetric } from '@/components/MobileMetric';
import { OnlinePrice, PriceDiff } from '@/components/PriceCells';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { BulkEditActions } from '@/components/bulk/BulkEditActions';
import type { BulkTargets } from '@/components/bulk/types';
import { useProducts } from '@/hooks/useProducts';
import { useProductVariants } from '@/hooks/useVariants';
import { useRowSelection } from '@/hooks/useRowSelection';
import { usePagination } from '@/hooks/usePagination';
import { fetchVariantsByProductIds } from '@/lib/api';
import type { ProductRow } from '@/types';

/**
 * The expandable panel beneath a product row — every variant of that product,
 * editable inline via the shared {@link VariantEditTable}. Lazily loaded when
 * the row is opened.
 */
function ProductVariantsPanel({ shopifyProductId }: { shopifyProductId: number }) {
  const { data: variants, isLoading, error } = useProductVariants(shopifyProductId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-4 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading variants…
      </div>
    );
  }
  if (error) {
    return <div className="px-4 py-4 text-xs text-destructive">Failed to load variants.</div>;
  }

  return (
    <div className="px-3 py-3 sm:px-4">
      <VariantEditTable variants={variants ?? []} />
    </div>
  );
}

function ProductMobileCard({
  item,
  context,
}: {
  item: ProductRow;
  context: DataTableMobileRenderContext;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        {context.selectable && (
          <div data-no-row-click className="pt-1" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={context.isSelected}
              onCheckedChange={context.toggleSelected}
              aria-label={`Select ${item.title}`}
            />
          </div>
        )}
        {item.image_url ? (
          <img
            src={item.image_url}
            alt=""
            className="h-14 w-14 shrink-0 rounded-md bg-muted object-cover"
          />
        ) : (
          <div className="h-14 w-14 shrink-0 rounded-md bg-muted" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="line-clamp-2 text-sm font-medium leading-5 text-foreground">
                {item.title}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <ProductStatusBadge status={item.status} className="text-[10px]" />
                {item.variant_count > 1 && (
                  <Badge variant="outline" className="text-[10px]">
                    {item.variant_count} variants
                  </Badge>
                )}
              </div>
            </div>
            <ProductLinks handle={item.handle} shopifyProductId={item.shopify_product_id} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MobileMetric label="Online">
          <OnlinePrice
            price={item.online_price}
            compareAtPrice={item.online_compare_at_price}
            isOnSale={item.is_on_sale}
          />
        </MobileMetric>
        <MobileMetric label="Nursery">
          <NurseryPriceEditor
            shopifyVariantId={item.shopify_variant_id}
            value={item.at_nursery_price}
          />
        </MobileMetric>
        <MobileMetric label="Diff">
          <PriceDiff online={item.online_price} nursery={item.at_nursery_price} />
        </MobileMetric>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className={cn('shrink-0 font-mono', inventoryColorClass(item.inventory_quantity))}>
          Qty {item.inventory_quantity}
        </span>
        <button
          type="button"
          data-no-row-click
          onClick={(e) => {
            e.stopPropagation();
            context.toggleExpanded();
          }}
          className="inline-flex items-center gap-1 rounded px-1.5 py-1 font-medium text-primary hover:bg-primary/10"
        >
          <ChevronRight
            className={cn('h-3.5 w-3.5 transition-transform', context.isExpanded && 'rotate-90')}
          />
          {context.isExpanded ? 'Hide variants' : 'Edit variants'}
        </button>
      </div>
    </div>
  );
}

const columns: DataTableColumn<ProductRow>[] = [
  {
    key: 'title',
    label: 'Product',
    sortable: true,
    className: 'max-w-[300px]',
    render: (item) => (
      <div className="flex min-w-0 items-center gap-3">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt=""
            className="h-8 w-8 shrink-0 rounded bg-muted object-cover"
          />
        ) : (
          <div className="h-8 w-8 shrink-0 rounded bg-muted" />
        )}
        <div className="min-w-0">
          <p className="truncate text-sm text-foreground">{item.title}</p>
          {item.variant_count > 1 && (
            <p className="text-[10px] text-muted-foreground">{item.variant_count} variants</p>
          )}
        </div>
      </div>
    ),
  },
  {
    key: 'links',
    label: 'Links',
    className: 'w-[60px]',
    render: (item) => (
      <ProductLinks handle={item.handle} shopifyProductId={item.shopify_product_id} />
    ),
  },
  {
    key: 'product_type',
    label: 'Type',
    sortable: true,
    className: 'w-[130px]',
    render: (item) => (
      <span className="truncate text-xs text-muted-foreground">{item.product_type ?? '—'}</span>
    ),
  },
  {
    key: 'online_price',
    label: 'Online',
    sortable: true,
    className: 'w-[150px]',
    render: (item) => (
      <OnlinePrice
        price={item.online_price}
        compareAtPrice={item.online_compare_at_price}
        isOnSale={item.is_on_sale}
      />
    ),
  },
  {
    key: 'at_nursery_price',
    label: 'Nursery',
    sortable: true,
    className: 'w-[100px]',
    render: (item) => (
      <NurseryPriceEditor
        shopifyVariantId={item.shopify_variant_id}
        value={item.at_nursery_price}
      />
    ),
  },
  {
    key: 'price_diff',
    label: 'Diff',
    sortable: true,
    className: 'w-[80px]',
    render: (item) => <PriceDiff online={item.online_price} nursery={item.at_nursery_price} />,
  },
  {
    key: 'inventory_quantity',
    label: 'Qty',
    sortable: true,
    className: 'w-[60px]',
    render: (item) => (
      <span className={cn('text-sm font-mono', inventoryColorClass(item.inventory_quantity))}>
        {item.inventory_quantity}
      </span>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    className: 'w-[90px]',
    render: (item) => <ProductStatusBadge status={item.status} className="text-[10px]" />,
  },
];

const STATUS_CHIPS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

const PRICE_CHIPS = [
  { value: 'all', label: 'All prices' },
  { value: 'on_sale', label: 'On sale' },
  { value: 'no_nursery', label: 'Missing nursery' },
  { value: 'nursery_lower', label: 'Nursery lower' },
  { value: 'nursery_higher', label: 'Nursery higher' },
  { value: 'same', label: 'Same price' },
];

/**
 * The Products list — products grouped one per row, each expandable to reveal
 * and edit its variants inline. Checkbox products and use the floating action
 * bar for bulk edits (variant-level edits apply to every variant of the
 * selected products). For per-variant multi-select, use the Variants page.
 */
export function ProductsPage() {
  const navigate = useNavigate();
  const {
    page,
    perPage,
    search,
    sort,
    setPage,
    setSearch,
    setSort,
    getSearchPath,
  } = usePagination({
    initialSort: { field: 'title', direction: 'asc' },
  });

  const [statusFilter, setStatusFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const selection = useRowSelection<ProductRow>((r) => r.id);

  const { data, isLoading, isFetching } = useProducts({
    page,
    per_page: perPage,
    search,
    sort_by: sort.field,
    sort_dir: sort.direction,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    price_comparison: priceFilter !== 'all' ? priceFilter : undefined,
  });

  const rows = data?.data ?? [];

  const handleStatusFilter = useCallback(
    (value: string) => {
      setStatusFilter(value);
      setPage(1);
    },
    [setPage],
  );
  const handlePriceFilter = useCallback(
    (value: string) => {
      setPriceFilter(value);
      setPage(1);
    },
    [setPage],
  );
  const handleRowClick = useCallback(
    (item: ProductRow) =>
      navigate(getSearchPath(`/products/${item.shopify_product_id}`)),
    [getSearchPath, navigate],
  );

  const filtersActive = statusFilter !== 'all' || priceFilter !== 'all';

  const filterBar = (
    <div className="flex min-w-0 flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-x-4 md:gap-y-2">
      <FilterChips
        options={STATUS_CHIPS}
        value={statusFilter}
        onChange={handleStatusFilter}
        className="w-full md:w-auto"
      />
      <FilterChips
        options={PRICE_CHIPS}
        value={priceFilter}
        onChange={handlePriceFilter}
        className="w-full md:w-auto"
      />
      {filtersActive && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 justify-center text-xs md:h-7"
          onClick={() => {
            setStatusFilter('all');
            setPriceFilter('all');
            setPage(1);
          }}
        >
          Clear filters
        </Button>
      )}
    </div>
  );

  const selectedProducts = selection.getSelectedRows();
  const productIds = selectedProducts.map((p) => p.shopify_product_id);
  const targets: BulkTargets = {
    selectionLabel: `${selection.count} ${selection.count === 1 ? 'product' : 'products'}`,
    productShopifyIds: productIds,
    resolveVariants: () => fetchVariantsByProductIds(productIds),
  };

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <DataTable
          columns={columns}
          data={rows}
          total={data?.total ?? 0}
          page={page}
          perPage={perPage}
          sort={sort}
          search={search}
          isLoading={isLoading}
          isFetching={isFetching}
          selectable
          expandable
          selectedIds={selection.selectedIds}
          getRowId={(item) => item.id}
          renderExpanded={(item) => (
            <ProductVariantsPanel shopifyProductId={item.shopify_product_id} />
          )}
          onPageChange={setPage}
          onSortChange={setSort}
          onSearchChange={setSearch}
          onSelectionChange={(next) => selection.setSelection(next, rows)}
          onRowClick={handleRowClick}
          searchPlaceholder="Search by name, SKU, vendor, type..."
          emptyMessage="No products found"
          emptySubMessage="Sync your Shopify catalog or adjust your search"
          filterBar={filterBar}
          mobileRender={(item, context) => (
            <ProductMobileCard item={item} context={context} />
          )}
        />
      </div>

      {/* Reserve space so the floating bar never covers the pagination footer. */}
      {selection.count > 0 && <div className="h-[80px] shrink-0 md:h-[88px]" />}

      <BulkActionBar count={selection.count} itemNoun="product" onClear={selection.clear}>
        <BulkEditActions targets={targets} onApplied={selection.clear} />
      </BulkActionBar>
    </div>
  );
}
